import { memo, useContext } from 'react';
import ErrorBoundary from '@/srp/ErrorBoundary';
import { LOW_RESULTS_THRESHOLD } from '~/constants/srp';
import TileType from '~/constants/srp/TileType';
import InventoryContext, {
  useIsInventoryLoaded,
  useSearchRequestId
} from '~/context/srp/InventoryContext';
import { TileData } from '~/context/srp/TileDataContext';
import type { ConditionalTile } from '~/contracts/srp';
import { useTiles } from './hooks';
import LowResults from './LowResultsSection';
import { ResultsSection as StyledResultsSection } from './ResultsSection.styles';
import ZeroResultsSection from './ZeroResultsSection';
import DownPaymentTile from '../DownPaymentTile';
import FinanceTile from '../FinanceTile';
import { usePagination } from '../hooks';
import KeepMovingTile from '../KeepMovingTile';
import Pagination from '../Pagination';
import ResultTile from '../ResultTile';
import SaveSearchTile from '../SaveSearchTile';
import TradeYourCar from '../TradeYourCarTile';
import useShowZeroResults from '../useShowZeroResults';

const isVehicle = (tile: ConditionalTile | TileData): tile is TileData =>
  'vehicleId' in tile;

function useGetTileDataLoaded() {
  const searchRequestId = useSearchRequestId();
  const inventoryLoaded = useIsInventoryLoaded();

  return Boolean(inventoryLoaded && searchRequestId);
}

export default memo(function ResultsSection() {
  const {
    similarVehicles: { vehicles: similarVehicles }
  } = useContext(InventoryContext);

  const isLoaded = useGetTileDataLoaded();
  const { pagination } = usePagination();
  const tiles = useTiles();

  const hasTiles = tiles.length > 0;
  const showZeroResults = useShowZeroResults(!hasTiles);
  const showLowResults =
    hasTiles &&
    pagination.totalMatchedPages === 1 &&
    similarVehicles.length > 0 &&
    tiles.length < LOW_RESULTS_THRESHOLD &&
    isLoaded;

  const showPagination =
    hasTiles && isLoaded && (pagination.totalMatchedPages ?? 0) >= 2;

  return (
    <>
      {isLoaded ? null : (
        <StyledResultsSection
          id="results-section"
          data-qa="search-results-loading"
          isLoading={!isLoaded}
          handleFavorite={() => {}}
        >
          {Array.from(Array(24).keys()).map(x => (
            <ResultTile key={x} />
          ))}
        </StyledResultsSection>
      )}
      {isLoaded && showZeroResults && (
        <ErrorBoundary>
          <ZeroResultsSection />

          <StyledResultsSection>
            {Array(4).map((_, index) => (
              <div key={index} />
            ))}
          </StyledResultsSection>
        </ErrorBoundary>
      )}
      {isLoaded && hasTiles && (
        <StyledResultsSection
          id="results-section"
          property="itemOffered"
          typeof="Car"
        >
          {tiles.map(tile => {
            if (isVehicle(tile)) {
              const vehicle: TileData = {
                ...tile,
                isSimilarTile: false
              };

              return <ResultTile tileData={vehicle} key={vehicle.vehicleId} />;
            }

            switch (tile.tileType) {
              case TileType.DownPayment:
                return <DownPaymentTile key="finance-tile" />;
              case TileType.Finance:
                return <FinanceTile key="finance-tile" />;
              case TileType.GreatDeal:
                return <KeepMovingTile key="keep-moving-tile" />;
              case TileType.SaveSearch:
                return <SaveSearchTile key="save-search-tile" />;
              case TileType.TradeIn:
                return <TradeYourCar key="trade-in-tile" />;
              default:
                return null;
            }
          })}
        </StyledResultsSection>
      )}
      {showLowResults && <LowResults />}
      {showPagination && <Pagination />}
    </>
  );
});
